/**
 * Gateways — full-page fleet gateway list at /app/gateways.
 * Tabs: Devices | Deployments. Summary bar, filters, table; deployment list + detail.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Radio, Plus, MapPin, Package, Layers } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { useGatewayDevices, type FleetGatewayDevice } from '@/hooks/useGatewayDevices';
import { useGatewayDeployments } from '@/hooks/useGatewayDeployments';
import { useGatewayProfiles, useDeleteGatewayProfile } from '@/hooks/useGatewayProfiles';
import { getWifiQuality } from '@/types/gateway';
import { AddGatewayOrgDialog } from '@/components/gateway/AddGatewayOrgDialog';
import { AssignGatewayDialog } from '@/components/gateway/AssignGatewayDialog';
import { CreateDeploymentDialog } from '@/components/gateway/CreateDeploymentDialog';
import { DeploymentDetailView } from '@/components/gateway/DeploymentDetailView';
import { GatewayProfileFormDialog } from '@/components/gateway/GatewayProfileFormDialog';
import type { GatewayProfile } from '@/types/gateway';

const HEARTBEAT_ONLINE_MS = 2 * 60 * 1000;
const HEARTBEAT_OFFLINE_MS = 5 * 60 * 1000;

export type GatewayRowStatus = 'online' | 'offline' | 'updating' | 'error';

function deriveStatus(d: FleetGatewayDevice): GatewayRowStatus {
  const now = Date.now();
  const last = d.last_heartbeat_at ? new Date(d.last_heartbeat_at).getTime() : 0;
  const ago = last ? now - last : Infinity;
  if (d.operational_status === 'degraded') return 'error';
  if (d.lifecycle_status === 'commissioning') return 'updating';
  if (ago <= HEARTBEAT_ONLINE_MS && d.operational_status === 'healthy') return 'online';
  if (ago > HEARTBEAT_OFFLINE_MS || !d.last_heartbeat_at || d.operational_status === 'offline') return 'offline';
  return 'online';
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
  { value: 'updating', label: 'Updating' },
  { value: 'error', label: 'Error' },
];

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'status', label: 'Status (offline first)' },
  { value: 'last_seen', label: 'Last seen (stalest first)' },
  { value: 'name', label: 'Name' },
  { value: 'firmware', label: 'Firmware version' },
];

const STATUS_BADGE: Record<GatewayRowStatus, { label: string; className: string }> = {
  online: { label: 'Online', className: 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20' },
  offline: { label: 'Offline', className: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
  updating: { label: 'Updating', className: 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20' },
  error: { label: 'Error', className: 'bg-wt-red-primary/10 text-wt-red-primary border-wt-red-primary/20' },
};

export default function Gateways() {
  const navigate = useNavigate();
  const { devices, isLoading, error } = useGatewayDevices();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [firmwareFilter, setFirmwareFilter] = useState<string>('all');
  const [propertyFilter, setPropertyFilter] = useState<string>('all');
  const [sort, setSort] = useState<string>('status');
  const [addOpen, setAddOpen] = useState(false);
  const [assignDeviceId, setAssignDeviceId] = useState<string | null>(null);
  const [deploymentsCreateOpen, setDeploymentsCreateOpen] = useState(false);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string | null>(null);

  const { deployments } = useGatewayDeployments();
  const { profiles } = useGatewayProfiles();
  const deleteProfile = useDeleteGatewayProfile();
  const [profilesCreateOpen, setProfilesCreateOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<GatewayProfile | null>(null);

  const firmwareVersions = useMemo(() => {
    const set = new Set<string>();
    devices.forEach((d) => {
      if (d.firmware_version) set.add(d.firmware_version);
    });
    return Array.from(set).sort();
  }, [devices]);

  const propertyOptions = useMemo(() => {
    const names = new Map<string | null, string>();
    names.set(null, 'Unassigned');
    devices.forEach((d) => {
      if (d.property_id && d.property_name) names.set(d.property_id, d.property_name);
    });
    return Array.from(names.entries()).map(([id, name]) => ({ id: id ?? '', name: name ?? 'Unassigned' }));
  }, [devices]);

  const filteredAndSorted = useMemo(() => {
    let list = devices.map((d) => ({ device: d, status: deriveStatus(d) }));
    if (statusFilter !== 'all') {
      list = list.filter((x) => x.status === statusFilter);
    }
    if (firmwareFilter !== 'all') {
      list = list.filter((x) => x.device.firmware_version === firmwareFilter);
    }
    if (propertyFilter === 'unassigned') {
      list = list.filter((x) => !x.device.property_id);
    } else if (propertyFilter !== 'all') {
      list = list.filter((x) => x.device.property_id === propertyFilter);
    }
    const statusOrder: Record<GatewayRowStatus, number> = { offline: 0, error: 1, updating: 2, online: 3 };
    if (sort === 'status') {
      list = [...list].sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);
    } else if (sort === 'last_seen') {
      list = [...list].sort((a, b) => {
        const ta = a.device.last_heartbeat_at ? new Date(a.device.last_heartbeat_at).getTime() : 0;
        const tb = b.device.last_heartbeat_at ? new Date(b.device.last_heartbeat_at).getTime() : 0;
        return ta - tb;
      });
    } else if (sort === 'name') {
      list = [...list].sort((a, b) => {
        const na = (a.device.mac_address ?? a.device.id).toLowerCase();
        const nb = (b.device.mac_address ?? b.device.id).toLowerCase();
        return na.localeCompare(nb);
      });
    } else if (sort === 'firmware') {
      list = [...list].sort((a, b) => (a.device.firmware_version ?? '').localeCompare(b.device.firmware_version ?? ''));
    }
    return list;
  }, [devices, statusFilter, firmwareFilter, propertyFilter, sort]);

  const profileDeviceCount = useMemo(() => {
    const count: Record<string, number> = {};
    profiles.forEach((p) => { count[p.id] = 0; });
    devices.forEach((d) => {
      if (d.profile_id && count[d.profile_id] !== undefined) count[d.profile_id]++;
    });
    return count;
  }, [profiles, devices]);

  const defaultProfile = useMemo(() => profiles.find((p) => p.is_default) ?? null, [profiles]);

  const summary = useMemo(() => {
    let online = 0, offline = 0, updating = 0, error = 0;
    devices.forEach((d) => {
      const s = deriveStatus(d);
      if (s === 'online') online++;
      else if (s === 'offline') offline++;
      else if (s === 'updating') updating++;
      else error++;
    });
    return { total: devices.length, online, offline, updating, error };
  }, [devices]);

  if (isLoading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-6 h-6 animate-spin text-wt-text-secondary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-6">
            <p className="text-sm text-wt-red-primary text-center">Failed to load gateways</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2">
          <Radio className="w-5 h-5" />
          Gateways
        </h1>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="w-4 h-4 mr-1.5" />
          Add Gateway
        </Button>
      </div>

      <Tabs defaultValue="devices" className="space-y-4">
        <TabsList className="bg-wt-bg-tertiary border border-border">
          <TabsTrigger value="devices" className="data-[state=active]:bg-wt-bg-secondary">
            Devices
          </TabsTrigger>
          <TabsTrigger value="deployments" className="data-[state=active]:bg-wt-bg-secondary">
            <Package className="w-3.5 h-3.5 mr-1.5" />
            Deployments
          </TabsTrigger>
          <TabsTrigger value="profiles" className="data-[state=active]:bg-wt-bg-secondary">
            <Layers className="w-3.5 h-3.5 mr-1.5" />
            Profiles
          </TabsTrigger>
        </TabsList>

        <TabsContent value="devices" className="space-y-4 mt-0">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <SummaryCard label="Total" value={summary.total} />
            <SummaryCard label="Online" value={summary.online} color="text-wt-green-primary" />
            <SummaryCard label="Offline" value={summary.offline} color="text-gray-400" />
            <SummaryCard label="Updating" value={summary.updating} color="text-wt-amber-primary" />
            <SummaryCard label="Error" value={summary.error} color="text-wt-red-primary" />
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] bg-wt-bg-secondary border-border">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={firmwareFilter} onValueChange={setFirmwareFilter}>
              <SelectTrigger className="w-[140px] bg-wt-bg-secondary border-border">
                <SelectValue placeholder="Firmware" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All versions</SelectItem>
                {firmwareVersions.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={propertyFilter} onValueChange={setPropertyFilter}>
              <SelectTrigger className="w-[160px] bg-wt-bg-secondary border-border">
                <SelectValue placeholder="Property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {propertyOptions.filter((o) => o.id).map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[180px] bg-wt-bg-secondary border-border">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-wt-text-primary">
                Gateway Devices
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {filteredAndSorted.length === 0 ? (
                <div className="py-8 text-center text-wt-text-secondary text-sm">
                  No gateways match the current filters.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-wt-text-secondary text-left">
                        <th className="px-4 py-2 font-medium">Name / Serial</th>
                        <th className="px-4 py-2 font-medium">Property</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                        <th className="px-4 py-2 font-medium">Firmware</th>
                        <th className="px-4 py-2 font-medium">Last seen</th>
                        <th className="px-4 py-2 font-medium">WiFi</th>
                        <th className="px-4 py-2 font-medium w-32" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSorted.map(({ device, status }) => (
                        <GatewayRow
                          key={device.id}
                          device={device}
                          status={status}
                          onNavigate={() => {
                            if (device.property_id) navigate(`/app/property/${device.property_id}/gateway`);
                          }}
                          onAssign={() => setAssignDeviceId(device.id)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deployments" className="mt-0">
          {selectedDeploymentId ? (
            <DeploymentDetailView
              deploymentId={selectedDeploymentId}
              onBack={() => setSelectedDeploymentId(null)}
            />
          ) : (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setDeploymentsCreateOpen(true)}>
                  <Plus className="w-4 h-4 mr-1.5" />
                  Create deployment
                </Button>
              </div>
              <Card className="bg-wt-bg-secondary border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-wt-text-primary">
                    Rollouts
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {deployments.length === 0 ? (
                    <div className="py-8 text-center text-wt-text-secondary text-sm">
                      No deployments yet. Create one to roll out firmware to gateways.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-wt-text-secondary text-left">
                            <th className="px-4 py-2 font-medium">Name</th>
                            <th className="px-4 py-2 font-medium">Firmware</th>
                            <th className="px-4 py-2 font-medium">Strategy</th>
                            <th className="px-4 py-2 font-medium">Status</th>
                            <th className="px-4 py-2 font-medium">Progress</th>
                            <th className="px-4 py-2 font-medium">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deployments.map((d) => (
                            <tr
                              key={d.id}
                              className="border-b border-border/50 hover:bg-wt-bg-tertiary/50 cursor-pointer"
                              onClick={() => setSelectedDeploymentId(d.id)}
                            >
                              <td className="px-4 py-2 text-wt-text-primary">
                                {d.name || `Deployment ${d.id.slice(0, 8)}`}
                              </td>
                              <td className="px-4 py-2 font-mono-numeric text-wt-text-secondary">
                                {d.firmware_version ?? '—'}
                              </td>
                              <td className="px-4 py-2 text-wt-text-secondary">{d.strategy}</td>
                              <td className="px-4 py-2">
                                <Badge variant="outline" className="text-xs capitalize">
                                  {d.status.replace('_', ' ')}
                                </Badge>
                              </td>
                              <td className="px-4 py-2 font-mono-numeric text-wt-text-secondary">
                                {d.updated_gateways}/{d.total_gateways}
                                {d.failed_gateways > 0 && ` (${d.failed_gateways} failed)`}
                              </td>
                              <td className="px-4 py-2 text-wt-text-secondary">
                                {format(new Date(d.created_at), 'MMM d, yyyy')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AddGatewayOrgDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        defaultProfileId={defaultProfile?.id ?? undefined}
        defaultProfileName={defaultProfile?.name}
      />
      {profilesCreateOpen && (
        <GatewayProfileFormDialog
          open={profilesCreateOpen}
          onOpenChange={(open) => {
            setProfilesCreateOpen(open);
            if (!open) setEditingProfile(null);
          }}
          profile={editingProfile}
          onSaved={() => { setProfilesCreateOpen(false); setEditingProfile(null); }}
        />
      )}
      {assignDeviceId && (
        <AssignGatewayDialog
          deviceId={assignDeviceId}
          open={!!assignDeviceId}
          onOpenChange={(open) => { if (!open) setAssignDeviceId(null); }}
        />
      )}
      <CreateDeploymentDialog
        open={deploymentsCreateOpen}
        onOpenChange={setDeploymentsCreateOpen}
        onCreated={(id) => { setSelectedDeploymentId(id); setDeploymentsCreateOpen(false); }}
      />
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardContent className="py-3 px-4 text-center">
        <p className={`text-2xl font-mono-numeric font-semibold ${color ?? 'text-wt-text-primary'}`}>{value}</p>
        <p className="text-xs text-wt-text-secondary mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}

function GatewayRow({
  device,
  status,
  onNavigate,
  onAssign,
}: {
  device: FleetGatewayDevice;
  status: GatewayRowStatus;
  onNavigate: () => void;
  onAssign: () => void;
}) {
  const wifiQ = getWifiQuality(device.wifi_rssi);
  const displayName = device.mac_address ?? device.id.slice(0, 8);
  const hasProperty = !!device.property_id;

  return (
    <tr className="border-b border-border/50 hover:bg-wt-bg-tertiary/50">
      <td className="px-4 py-2 font-mono-numeric text-wt-text-primary">{displayName}</td>
      <td className="px-4 py-2 text-wt-text-primary">{device.property_name ?? 'Unassigned'}</td>
      <td className="px-4 py-2">
        <Badge variant="outline" className={`text-xs ${STATUS_BADGE[status].className}`}>
          {STATUS_BADGE[status].label}
        </Badge>
      </td>
      <td className="px-4 py-2 font-mono-numeric text-wt-text-secondary">{device.firmware_version ?? '--'}</td>
      <td className="px-4 py-2 text-wt-text-secondary">
        {device.last_heartbeat_at
          ? formatDistanceToNow(new Date(device.last_heartbeat_at), { addSuffix: true })
          : 'Never'}
      </td>
      <td className="px-4 py-2">
        <span className={`font-mono-numeric ${wifiQ.color}`}>
          {device.wifi_rssi != null ? `${device.wifi_rssi} dBm` : '--'}
        </span>
      </td>
      <td className="px-4 py-2">
        {hasProperty ? (
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onNavigate}>
            Open
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onAssign}>
            <MapPin className="w-3 h-3 mr-1" />
            Assign to property
          </Button>
        )}
      </td>
    </tr>
  );
}
